/**
 * Resumo PRELIMINAR copiável do encerramento na triagem (E45.F2.T03). // ux-neutrality:allow doc-comment
 *
 * Decisão do owner (2026-06-30): o "compartilhável" é um artefato COPIÁVEL na // ux-neutrality:allow doc-comment
 * tela (sem e-mail, sem destinatários, sem reusar o share da E24). O perito // ux-neutrality:allow doc-comment
 * copia o texto e encaminha ao requerente. Rotulado NÃO-PERICIAL — nunca é // ux-neutrality:allow doc-comment
 * laudo, nunca expõe LR. // ux-neutrality:allow doc-comment
 */
import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { ptBR } from '@/i18n/pt-BR';
import { Blueprint } from '@/components/ui/Blueprint';
import { Button } from '@/components/ui/Button';
import type { PreAnaliseRecordData } from '@/lib/pre_analise';
import { buildShareableSummary } from './labels';

export interface PreAnaliseShareSummaryProps {
  preAnalise: PreAnaliseRecordData;
}

export function PreAnaliseShareSummary({ preAnalise }: PreAnaliseShareSummaryProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const summary = buildShareableSummary(preAnalise);

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard?.writeText(summary);
      setCopied(true);
    } catch {
      // Sem acesso à área de transferência: o texto continua visível para
      // seleção/cópia manual — não há erro a propagar.
      setCopied(false);
    }
  }

  return (
    <Blueprint
      as="section"
      aria-labelledby="pre-analise-share-title"
      className="mt-sc-6 p-sc-6"
      data-testid="pre-analise-share"
    >
      <h2 id="pre-analise-share-title" className="text-heading-sm text-fg">
        {ptBR.preAnalise.shareSectionTitle}
      </h2>
      <p className="mt-1 text-body-sm text-fg-muted">{ptBR.preAnalise.shareIntro}</p>
      {/* O texto que o perito copia é o artefato: fica em mono, na superfície
          de papel, para que quebra de linha e acentuação apareçam como serão
          coladas. */}
      <pre
        className="mt-sc-3 whitespace-pre-wrap border border-border bg-paper p-sc-3 font-mono text-body-sm text-fg"
        data-testid="pre-analise-shareable-text"
      >
        {summary}
      </pre>
      <div className="mt-sc-3 flex items-center gap-sc-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void handleCopy()}
          data-testid="pre-analise-copy"
        >
          {copied ? (
            <Check className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Copy className="h-4 w-4" aria-hidden="true" />
          )}
          {copied ? ptBR.preAnalise.shareCopied : ptBR.preAnalise.shareCopyCta}
        </Button>
        <span className="sr-only" aria-live="polite">
          {copied ? ptBR.preAnalise.shareCopied : ''}
        </span>
      </div>
    </Blueprint>
  );
}
